import React, {
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { IconRowActions } from '../../icons';
import { useTranslation } from 'react-i18next';
import { Spinner } from './Spinner';
import { useAnchoredPosition } from './overlayPosition';

/**
 * `common/RowActionsMenu` (M-V4-A, architecture-ux-v4.md §1.3).
 *
 * Das wiederverwendbare Aktionen-Overflow-Menü der Tabellen-Welt (behebt D-V4-1:
 * die Aktions-Spalte frisst mobil bis zu 2/3 der Breite). EIN kompakter
 * ⋯-Trigger pro Zeile öffnet je nach Breakpoint einen Popover (Desktop, ≥640 px)
 * oder ein Bottom-Sheet (Mobile, <640 px) — beide portal-basiert an
 * `document.body`, damit sie dem clippenden `overflow-x-auto`-Scrollport der
 * DataTable entkommen (gleiches `overlayPosition`-Primitive wie StatusChip).
 *
 * Konsumierende Tabellen (OpenItems/OfferArchive/Expenses/UnifiedInbox) übergeben
 * ihre Aktionsliste deklarativ als `items` — inkl. konditionaler Sichtbarkeit
 * (leeres Array ⇒ die Komponente rendert `null`, kein toter Trigger), `danger`-
 * Variante, Button-Aktionen (`onSelect`) UND echten Router-Links (`to`).
 *
 * Verhaltens-Vertrag (§1.3, bindend): `role="menu"`/`menuitem`, Roving-Fokus mit
 * ↑/↓/Home/End, Escape/Outside/Backdrop schließen, Fokusfalle im Sheet, Trigger-
 * Klick `stopPropagation` (öffnet nie gleichzeitig die Zeile), `motion-safe`-only
 * Animationen.
 */

export interface RowActionsMenuItem {
  /** Stabil — dient als React-`key` und `data-testid` (`row-actions-item-<id>`). */
  id: string;
  /** i18n-übersetzt vom Aufrufer (Pflicht — nie nackter Key). */
  label: string;
  /** Optionales Icon (aria-hidden gerendert). */
  icon?: ReactNode;
  /** Farbsprache analog `RowAction` (§1.3). */
  variant?: 'default' | 'accent' | 'danger';
  disabled?: boolean;
  /** Spinner statt Icon; Item nicht aktivierbar. */
  loading?: boolean;
  /**
   * Design-v5 §6.5 (b): hebt das Item optisch als primäre Conversion-Aktion
   * hervor (z. B. „Verbuchen" als erstes Menü-Item) — Label in `--accent-ink`,
   * Icon in Mint (`--accent`). Additiv, unabhängig von `variant`; bestehende
   * Consumer ohne `emphasis` sind unverändert.
   */
  emphasis?: 'accent';
  /** Button-Aktion — XOR zu `to`. Wird NACH dem Schließen des Menüs aufgerufen. */
  onSelect?: () => void;
  /** Router-Ziel — rendert das Item als `<Link role="menuitem">` (Middle-Click/Neuer-Tab). */
  to?: string;
}

export interface RowActionsMenuProps {
  /** `[]` ⇒ Komponente rendert `null` (kein leerer Trigger). */
  items: RowActionsMenuItem[];
  /** Trigger-Label; Default `common:rowActions.trigger` („Aktionen"). Icon-only ⋯, Label als aria-label/title. */
  triggerLabel?: string;
  'data-testid'?: string;
}

const ITEM_VARIANT_CLASSES: Record<NonNullable<RowActionsMenuItem['variant']>, string> = {
  default: 'text-secondary hover:bg-surface-inset hover:text-primary focus:bg-surface-inset',
  accent: 'text-accent-ink hover:bg-surface-inset hover:text-accent-ink focus:bg-surface-inset',
  danger: 'text-[var(--tone-danger-fg)] hover:bg-[var(--tone-danger-bg)] hover:text-[var(--tone-danger-fg)] focus:bg-[var(--tone-danger-bg)]',
};

const ITEM_BASE_CLASSES = [
  'flex w-full items-center gap-2 px-3 text-left text-sm font-medium',
  'min-h-[44px] sm:min-h-[36px]', // Touch-Target im Sheet (≥44), kompakter im Popover.
  'transition-colors duration-150 outline-none',
  'aria-disabled:opacity-50 aria-disabled:cursor-not-allowed',
].join(' ');

type MenuMode = 'popover' | 'sheet';

/** Ob die Item-Position im Roving-Fokus anspringbar ist (disabled/loading werden übersprungen). */
function isNavigable(item: RowActionsMenuItem): boolean {
  return !item.disabled && !item.loading;
}

export function RowActionsMenu({
  items,
  triggerLabel,
  'data-testid': dataTestId = 'row-actions-menu',
}: RowActionsMenuProps) {
  const { t } = useTranslation();
  const label = triggerLabel ?? t('common:rowActions.trigger');

  const menuId = useId();
  const triggerId = useId();

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<MenuMode>('popover');
  const [activeIndex, setActiveIndex] = useState(-1);

  const { style: anchoredStyle } = useAnchoredPosition(
    open && mode === 'popover',
    triggerRef,
    menuRef,
    'bottom-end',
  );

  const firstNavigable = useCallback(
    () => items.findIndex(isNavigable),
    [items],
  );
  const lastNavigable = useCallback(() => {
    for (let i = items.length - 1; i >= 0; i -= 1) {
      if (isNavigable(items[i])) return i;
    }
    return -1;
  }, [items]);

  /** Bestimmt beim Öffnen Popover vs. Sheet; JSDOM ohne matchMedia ⇒ Popover-Fallback. */
  const resolveMode = useCallback((): MenuMode => {
    if (typeof window.matchMedia !== 'function') return 'popover';
    return window.matchMedia('(min-width: 640px)').matches ? 'popover' : 'sheet';
  }, []);

  const closeMenu = useCallback((returnFocus: boolean) => {
    setOpen(false);
    setActiveIndex(-1);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const openMenu = useCallback(
    (initial: 'first' | 'last') => {
      setMode(resolveMode());
      setOpen(true);
      setActiveIndex(initial === 'first' ? firstNavigable() : lastNavigable());
    },
    [resolveMode, firstNavigable, lastNavigable],
  );

  const activateItem = useCallback(
    (item: RowActionsMenuItem) => {
      if (item.disabled || item.loading) return;
      // Reihenfolge fix: erst schließen, DANN onSelect — so übernimmt ein
      // Folge-Modal den Fokus sauber (§1.3 #5). Kein Fokus-Rücksprung auf den
      // Trigger, damit er nicht mit dem Modal-Initialfokus kollidiert.
      closeMenu(false);
      item.onSelect?.();
    },
    [closeMenu],
  );

  // Roving-Fokus: fokussiert das aktive Item nach jedem Render.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    itemRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  // Body-Scroll-Lock solange das Bottom-Sheet offen ist (Muster wie Modal).
  useEffect(() => {
    if (!open || mode !== 'sheet') return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [open, mode]);

  // Outside-Klick schließt den Popover (Sheet nutzt Backdrop-Klick).
  useEffect(() => {
    if (!open || mode !== 'popover') return undefined;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      closeMenu(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, mode, closeMenu]);

  // Viewport-Wechsel über die 640px-Grenze schließt das Menü (deterministisch, §1.3 #2).
  useEffect(() => {
    if (!open || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(min-width: 640px)');
    const onChange = () => closeMenu(false);
    // addEventListener('change') ist der moderne Weg; addListener als Fallback.
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [open, closeMenu]);

  const moveActive = useCallback(
    (delta: 1 | -1) => {
      const navigable = items.reduce<number[]>((acc, item, index) => {
        if (isNavigable(item)) acc.push(index);
        return acc;
      }, []);
      if (navigable.length === 0) return;
      const current = navigable.indexOf(activeIndex);
      const nextPos = current === -1 ? 0 : (current + delta + navigable.length) % navigable.length;
      setActiveIndex(navigable[nextPos]);
    },
    [items, activeIndex],
  );

  const handleTriggerClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Zeilen tragen eigene onRowClick/Klick-Handler — ein Menü-Tap darf NIE
      // gleichzeitig die Zeile öffnen.
      event.stopPropagation();
      if (open) {
        closeMenu(false);
        return;
      }
      openMenu('first');
    },
    [open, closeMenu, openMenu],
  );

  const handleTriggerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!open) openMenu('first');
        else moveActive(1);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (!open) openMenu('last');
        else moveActive(-1);
      }
    },
    [open, openMenu, moveActive],
  );

  const handleMenuKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          moveActive(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          moveActive(-1);
          break;
        case 'Home':
          event.preventDefault();
          setActiveIndex(firstNavigable());
          break;
        case 'End':
          event.preventDefault();
          setActiveIndex(lastNavigable());
          break;
        case 'Escape':
          event.preventDefault();
          event.stopPropagation(); // DataTable-Zeilen-Keydown darf nichts mitbekommen.
          closeMenu(true);
          break;
        case 'Tab':
          if (mode === 'sheet') {
            // Fokusfalle: Items sind tabIndex=-1 ⇒ keine Tab-Stops, Fokus bleibt gefangen.
            event.preventDefault();
          } else {
            closeMenu(false);
          }
          break;
        default:
          break;
      }
    },
    [moveActive, firstNavigable, lastNavigable, closeMenu, mode],
  );

  if (items.length === 0) return null;

  const renderItemContent = (item: RowActionsMenuItem): ReactNode => (
    <>
      {item.loading ? (
        <Spinner size="sm" />
      ) : item.icon ? (
        <span
          className={['shrink-0 inline-flex', item.emphasis === 'accent' ? 'text-accent' : ''].join(' ')}
          aria-hidden="true"
        >
          {item.icon}
        </span>
      ) : null}
      <span className={['truncate', item.emphasis === 'accent' ? 'text-accent-ink' : ''].join(' ')}>
        {item.label}
      </span>
    </>
  );

  const renderItem = (item: RowActionsMenuItem, index: number): ReactNode => {
    const isDisabled = Boolean(item.disabled || item.loading);
    const className = [ITEM_BASE_CLASSES, ITEM_VARIANT_CLASSES[item.variant ?? 'default']].join(' ');
    const testId = `row-actions-item-${item.id}`;

    if (item.to && !isDisabled) {
      return (
        <Link
          key={item.id}
          ref={(el: HTMLAnchorElement | null) => {
            itemRefs.current[index] = el;
          }}
          to={item.to}
          role="menuitem"
          tabIndex={-1}
          data-testid={testId}
          className={className}
          onClick={() => closeMenu(false)}
        >
          {renderItemContent(item)}
        </Link>
      );
    }

    return (
      <button
        key={item.id}
        ref={(el) => {
          itemRefs.current[index] = el;
        }}
        type="button"
        role="menuitem"
        tabIndex={-1}
        data-testid={testId}
        aria-disabled={isDisabled || undefined}
        className={className}
        onClick={() => activateItem(item)}
      >
        {renderItemContent(item)}
      </button>
    );
  };

  const popover = (
    <div
      ref={menuRef}
      id={menuId}
      role="menu"
      aria-labelledby={triggerId}
      style={anchoredStyle}
      onKeyDown={handleMenuKeyDown}
      className={[
        'z-40 min-w-[12rem] w-max max-w-xs overflow-hidden py-1',
        'rounded-card border border-default bg-surface-raised shadow-[var(--shadow-overlay)]',
        'motion-safe:animate-[fb-chip-pop-in_120ms_ease-out]',
      ].join(' ')}
    >
      {items.map(renderItem)}
    </div>
  );

  const sheet = (
    <div className="fixed inset-0 z-50" role="presentation">
      <div
        className="fixed inset-0 bg-black/60 motion-safe:animate-[fb-chip-pop-in_150ms_ease-out]"
        aria-hidden="true"
        onClick={() => closeMenu(true)}
      />
      <div
        ref={menuRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onKeyDown={handleMenuKeyDown}
        className={[
          'fixed inset-x-0 bottom-0 pb-[env(safe-area-inset-bottom)]',
          'rounded-t-card border-t border-default bg-surface-raised shadow-[var(--shadow-overlay)]',
          'motion-safe:animate-[fb-advisor-pop-in_180ms_ease-out]',
        ].join(' ')}
      >
        <div className="flex flex-col items-center gap-1 px-4 pt-3 pb-2">
          <span className="h-1 w-10 rounded-full bg-surface-inset" aria-hidden="true" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</span>
        </div>
        <div role="menu" aria-labelledby={triggerId} className="pb-2">
          {items.map(renderItem)}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-testid={dataTestId}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        className={[
          'inline-flex items-center justify-center rounded-control text-secondary',
          'min-h-[32px] min-w-[32px] p-1.5 text-xs',
          'transition-colors duration-150 hover:bg-surface-inset hover:text-primary',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
        ].join(' ')}
      >
        <IconRowActions />
      </button>
      {open && mode === 'popover' ? createPortal(popover, document.body) : null}
      {open && mode === 'sheet' ? createPortal(sheet, document.body) : null}
    </>
  );
}

export default RowActionsMenu;
